import { ApiError, apiFetchRaw, apiRequest } from './client.js';

export type ReportKind =
  | 'pdf_executive'
  | 'docx_technical'
  | 'json_export'
  | 'pdf_findings'
  | 'pdf_closure'
  // Sprint Z · ADR-031 — informes IA
  | 'ai_opening'
  | 'ai_closure'
  | 'ai_nonconformities';

export interface ReportItem {
  id: string;
  kind: ReportKind;
  sha256: string;
  generated_at: string;
  download_url: string;
}

export const reportsApi = {
  list: (assessmentId: string) =>
    apiRequest<{ reports: ReportItem[] }>(`/api/v1/assessments/${assessmentId}/reports`),
  /**
   * Sprint A · Historia A1 — encola generación on-demand de un reporte.
   * Sprint Z · ADR-031 — añade kinds ai_*.
   * Devuelve 202 + { status:'pending', kind } sin reportId.
   * La UI debe re-fetchear list() para descubrir la nueva fila.
   */
  generate: (
    assessmentId: string,
    kind: 'pdf_findings' | 'pdf_executive' | 'ai_opening' | 'ai_closure' | 'ai_nonconformities',
  ) =>
    apiRequest<{ status: 'pending'; kind: ReportKind }>(
      `/api/v1/assessments/${assessmentId}/reports`,
      { method: 'POST', body: { kind } },
    ),
};

/** Mapa de extensión de archivo por kind — réplica del KIND_TO_EXT del backend. */
const KIND_TO_EXT: Record<ReportKind, string> = {
  pdf_executive: 'pdf',
  docx_technical: 'docx',
  json_export: 'json',
  pdf_findings: 'pdf',
  pdf_closure: 'pdf',
  // Sprint Z · ADR-031
  ai_opening: 'pdf',
  ai_closure: 'pdf',
  ai_nonconformities: 'pdf',
};

/**
 * Descarga autenticada de un reporte.
 *
 * El endpoint requiere `Authorization: Bearer` igual que el resto de la API.
 * Un `<a download>` nativo no inyecta ese header (navegación HTTP pura),
 * lo que provocaba que el backend respondiera 401 JSON y el navegador
 * descargara ese JSON como archivo en lugar del PDF/DOCX real.
 *
 * Patrón: fetch con Bearer → Blob → URL.createObjectURL → clic sintético → revoke.
 *
 * TODO(deuda-tecnica): las descargas vía Blob están limitadas a ~10 MB en
 * RAM del navegador. Para archivos más grandes (json_export masivo o
 * futuros reportes de auditoría consolidada) se deberá migrar a presigned
 * URLs de Object Storage (ver ADR-010 futuro citado en routes.ts).
 */
export async function downloadReport(
  assessmentId: string,
  reportId: string,
  kind: ReportKind,
): Promise<void> {
  const res = await apiFetchRaw(`/api/v1/assessments/${assessmentId}/reports/${reportId}/download`);
  if (!res.ok) {
    throw new ApiError(res.status, res.statusText);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `assessment-${assessmentId.slice(0, 8)}-${kind}.${KIND_TO_EXT[kind] ?? 'bin'}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function reportLabel(kind: ReportKind): string {
  switch (kind) {
    case 'pdf_executive':
      return 'PDF ejecutivo';
    case 'docx_technical':
      return 'DOCX técnico';
    case 'json_export':
      return 'JSON exportable';
    case 'pdf_findings':
      return 'PDF de hallazgos';
    case 'pdf_closure':
      return 'PDF de cierre de hallazgo';
    // Sprint Z · ADR-031
    case 'ai_opening':
      return 'Acta de apertura IA';
    case 'ai_closure':
      return 'Informe de cierre IA';
    case 'ai_nonconformities':
      return 'Informe de no conformidades IA';
    default:
      return kind;
  }
}

/**
 * Mapea un código de error del backend a un mensaje visible al auditor.
 * Sprint Z · ADR-031 — variantes de error para kinds ai_*.
 * Sprint AA · Z-001 — añade ai_auth_failed, ai_upstream_unavailable,
 *                      ai_invalid_output, ai_citations_missing.
 * La propagación async (worker-side errors → cliente) es DEUDA-AA-001.
 */
export function aiErrorMessage(code: string | undefined): string {
  switch (code) {
    case 'ai_not_configured':
      return 'El servicio de IA no está configurado para este workspace. Contacta al administrador.';
    case 'ai_auth_failed':
      return 'Credencial del servicio de IA inválida o vencida. Contacta al administrador.';
    case 'ai_rate_limited':
      return 'Límite temporal del servicio de IA. Espera unos minutos e intenta de nuevo.';
    case 'ai_upstream_unavailable':
      return 'El servicio de IA no responde en este momento. Intenta de nuevo en unos minutos.';
    case 'ai_timeout':
      return 'El informe está tardando más de lo habitual. Recarga en unos minutos o intenta de nuevo.';
    case 'ai_invalid_output':
      return 'El servicio de IA devolvió un resultado con formato incorrecto. Intenta de nuevo; si persiste, contacta a soporte.';
    case 'ai_citations_missing':
      return 'El informe generado contiene referencias inválidas. Intenta de nuevo; si persiste, contacta a soporte.';
    default:
      return 'El servicio de IA no está disponible. Intenta de nuevo en unos minutos.';
  }
}
